import { useState, useEffect } from "react";
import { UserProfile, ActionSuggestion } from "@/types/user";
import { useUser } from "@/contexts/UserContext";
import { WeeklyInsightData } from "./DashboardInsights";
import { fetchUserTeamDetails } from "../team/services/teamService";
import { organizationsService } from "@/services/organizationsService";
import { usersService } from "@/services/usersService";

export const useDashboardData = () => {
  const { user } = useUser();
  const [teamMembers, setTeamMembers] = useState<UserProfile[]>([]);
  const [weeklyInsightData, setWeeklyInsightData] = useState<WeeklyInsightData>(
    {
      tone: {
        positive: 0.6,
        neutral: 0.3,
        negative: 0.1,
      },
      topThemes: [
        { name: "Getting Started", count: 1 },
        { name: "Personal Growth", count: 1 },
      ],
      weeklyChanges: {
        energy: 0,
        focus: 0,
      },
      focusPatterns: {
        peakTimeRange: "Complete check-ins to discover your patterns.",
        recommendation: "Start daily check-ins to build insights.",
      },
      energyTrend: {
        percentageChange: 0,
        message: "Welcome! Start tracking to see your energy trends.",
      },
      upcomingActivities: {
        count: 0,
        message: "Ready to begin your growth journey.",
      },
      weeklySummary: {
        text: [
          "Welcome to your personal dashboard.",
          "Complete check-ins to unlock personalized insights.",
        ],
        recommendation: "Start with your first daily check-in to begin building your wellness profile.",
      },
    }
  );
  const [actionSuggestions, setActionSuggestions] = useState<
    ActionSuggestion[]
  >([]);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchOrganizationMembers = async () => {
    if (!user?.organizationId) {
      if (process.env.NODE_ENV === 'development') {
        console.log("No organization ID found");
      }
      setTeamMembers([]);
      return;
    }

    try {
      // apiService returns data directly, not { success, data }
      const membersData = await organizationsService.getMembers(user.organizationId);
      const members_arr = Array.isArray(membersData) 
        ? membersData 
        : (membersData?.members || []);

      if (!members_arr || members_arr.length === 0) {
        setTeamMembers([]);
        return;
      }

      // Transform to team member format
      const members = members_arr.map((member: any) => ({
        id: member.id,
        name: member.name,
        avatarUrl: member.avatarUrl,
        lastCheckIn: "Unknown",
        status: "active",
        role: "member",
        teamType: "organization" as const,
        skills: [] as string[]
      }));

      setTeamMembers(members);
    } catch (error) {
      console.error('Error fetching organization members:', error);
      setTeamMembers([]);
    }
  };

  const fetchTeamMembers = async () => {
    // Independent solo users should never see team members
    // Also include users with "individual" persona who have no team (legacy solo users)
    if (user?.persona === "independentSolo" || 
        (user?.persona === "individual" && !user?.teamId && !user?.organizationId)) {
      if (process.env.NODE_ENV === 'development') {
        console.log("Independent solo user detected, setting empty team members");
      }
      setTeamMembers([]);
      return;
    }

    // Organization solo users should see organization members
    if (user?.persona === "organizationalSolo") {
      if (process.env.NODE_ENV === 'development') {
        console.log("Organization solo user detected, loading organization members");
      }
      await fetchOrganizationMembers();
      return;
    }
    
    if (!user?.teamId) {
      if (process.env.NODE_ENV === 'development') {
        console.log("User has no team, setting empty team members");
        console.log("User persona:", user?.persona);
      }
      // Don't return early for new team members - they might still be getting set up
      if (user?.persona !== "newTeamMember" && user?.persona !== "newJoiner") {
        setTeamMembers([]);
        return;
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log("Fetching team members for teamId:", user.teamId);
    }
    try {
      const membersData = await fetchUserTeamDetails(user.teamId);
      // fetchUserTeamDetails now returns empty array instead of null on error
      if (Array.isArray(membersData)) {
        // Filter out the current user from the team members list
        const filteredMembers = membersData.filter(
          (member) => member.id !== user.id
        );
        if (process.env.NODE_ENV === 'development') {
          console.log("Team members loaded:", filteredMembers.length, "members");
        }
        setTeamMembers(filteredMembers);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log("No team members data returned");
        }
        setTeamMembers([]);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error fetching team members:", error);
      }
      setTeamMembers([]);
    }
  };
  // Fetch team members based on user context
  useEffect(() => {
    if (user) {
      // Solo users should never see team members
      const isSoloUser = user.persona === "independentSolo" || user.persona === "organizationalSolo" ||
        (user.persona === "individual" && !user.teamId && !user.organizationId);
      
      if (isSoloUser) {
        setTeamMembers([]);
      } else {
        fetchTeamMembers();
      }
    } else {
      setTeamMembers([]);
    }
  }, [user]);

  // Initialize action suggestions with static data (can be replaced with AI-generated suggestions later)
  useEffect(() => {
    const initializeActionSuggestions = () => {
      const suggestions: ActionSuggestion[] = [
        {
          id: "1",
          title: "Schedule Focus Time",
          description:
            "Block out 2 hours for deep work on your priority project",
          actionType: "focus",
          priority: "high",
        },
        {
          id: "2",
          title: "Team Check-in",
          description:
            "Some team members may need support based on recent check-ins",
          actionType: "connect",
          priority: "medium",
        },
        {
          id: "3",
          title: "Take a break",
          description:
            "You've been working hard - schedule a short break to recharge",
          actionType: "break",
          priority: "low",
        },
        {
          id: "4",
          title: "Reflect on goals",
          description: "Review your quarterly objectives and current progress",
          actionType: "reflect",
          priority: "medium",
        },
        {
          id: "5",
          title: "Celebrate progress",
          description:
            "You've made significant progress on your design project",
          actionType: "celebrate",
          priority: "low",
        },
      ];

      setActionSuggestions(suggestions);
    };

    initializeActionSuggestions();
  }, []);

  return {
    teamMembers,
    weeklyInsightData,
    actionSuggestions,
    activeTab,
    setActiveTab,
  };
};
